import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
    Button,
    ConfirmDialog,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    formatPhone,
    gtagEvent,
    stripCountryCode,
    vars,
} from '@agendor/ui';
import {
    FormGroup,
    InputGroup,
    TextArea,
    HelperText,
    Checkbox,
    CheckboxLabel,
    LabelRow,
    Select,
    Divider,
} from '../components/Input';
import { Modal } from '../components/Modal';
import {
    MasterDetail,
    MasterList,
    MasterItem,
    MasterItemMeta,
    DetailPanel,
    EmptyState,
    DetailPlaceholder,
} from '../components/MasterDetail';
import BusinessService, {
    BusinessDetails,
    BookingReminder,
    DuplicateIntakeNameError,
    MAX_BOOKING_REMINDERS,
    hasPaidAccess,
} from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { OpeningHoursEditor } from '../components/OpeningHoursEditor';
import {
    WorkerOpeningHoursField,
    WorkerOpeningHoursModal,
    summarizeWorkerOpeningHours,
} from '../components/WorkerOpeningHours';
import {
    OpeningHoursDraft,
    PlainOpeningHours,
    WEEKDAYS,
    emptyDraft,
    formatPlainTime,
    fromDraft,
    summarizeOpeningHours,
    toDraft,
} from '../opening-hours';
import { useToast } from '../toast-context';
import { validateEmail } from '../email-validation';
import SubscriptionSection from './SubscriptionSection';
import { OwnerPhoneChangeModal } from './OwnerPhoneChangeModal';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const ConfigList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ConfigCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const InfoCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: default;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const ConfigInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ConfigTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ConfigSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #666;
    font-size: 0.9rem;
`;

const SummaryItem = styled.span`
    color: #666;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
`;

const UpgradeHint = styled.div`
    background: ${vars.backgroundColorDark};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;

    p {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        color: #666;
    }
`;

type ProfileState = {
    name: string;
    slug: string;
    description: string;
    ownerPhone: string;
    ownerEmail: string | null;
    botEnabled: boolean;
    calendarUrl: string;
};

type BookingConfigState = {
    minHoursNotice: number | null;
    maxDaysAhead: number | null;
    maxDurationInMinutes: number | null;
    maxConcurrent: number | null;
    checkInEnabled: boolean;
    bookingPageEnabled: boolean;
};

type BookingReminderDraft =
    | { kind: 'minutesBefore'; minutes: string }
    | { kind: 'daysBefore'; daysBefore: string; time: string };

type NotificationConfigState = {
    remindersEnabled: boolean;
    reminders: BookingReminderDraft[];
    confirmationsEnabled: boolean;
    confirmationDaysBefore: number;
    confirmationTime: string;
};

const emptyProfile: ProfileState = {
    name: '',
    slug: '',
    description: '',
    ownerPhone: '',
    ownerEmail: null,
    botEnabled: false,
    calendarUrl: '',
};

const emptyBookingConfig: BookingConfigState = {
    minHoursNotice: null,
    maxDaysAhead: null,
    maxDurationInMinutes: null,
    maxConcurrent: null,
    checkInEnabled: false,
    bookingPageEnabled: true,
};

const emptyNotificationConfig: NotificationConfigState = {
    remindersEnabled: false,
    reminders: [],
    confirmationsEnabled: false,
    confirmationDaysBefore: 1,
    confirmationTime: '09:00',
};

const newReminderDraft = (): BookingReminderDraft => ({ kind: 'minutesBefore', minutes: '60' });

const reminderToDraft = (reminder: BookingReminder): BookingReminderDraft => {
    if (reminder.kind === 'minutesBefore') {
        return { kind: 'minutesBefore', minutes: String(reminder.minutes) };
    }
    return { kind: 'daysBefore', daysBefore: String(reminder.daysBefore), time: formatPlainTime(reminder.time) };
};

const draftToReminder = (draft: BookingReminderDraft): BookingReminder | { error: string } => {
    if (draft.kind === 'minutesBefore') {
        const minutes = Number(draft.minutes);
        if (!Number.isInteger(minutes) || minutes <= 0) {
            return { error: 'Os minutos antes do lembrete devem ser um número inteiro maior que zero.' };
        }
        return { kind: 'minutesBefore', minutes };
    }
    const daysBefore = Number(draft.daysBefore);
    if (!Number.isInteger(daysBefore) || daysBefore < 0) {
        return { error: 'Os dias antes do lembrete devem ser um número inteiro maior ou igual a zero.' };
    }
    const timeMatch = draft.time.match(/^(\d{2}):(\d{2})$/);
    if (!timeMatch) {
        return { error: 'O horário do lembrete deve estar no formato HH:MM.' };
    }
    const hour = parseInt(timeMatch[1], 10);
    const minute = parseInt(timeMatch[2], 10);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return { error: 'O horário do lembrete deve ser válido (00:00 a 23:59).' };
    }
    return { kind: 'daysBefore', daysBefore, time: { hour, minute } };
};

const reminderUniqueKey = (reminder: BookingReminder): string =>
    reminder.kind === 'minutesBefore'
        ? `m:${reminder.minutes}`
        : `d:${reminder.daysBefore}:${reminder.time.hour}:${reminder.time.minute}`;

type IntakeQuestionDraft = {
    id: number | null;
    questionText: string;
    type: 'text' | 'textarea' | 'boolean' | 'enum';
    options: string[];
    required: boolean;
};

type IntakeDraft = {
    /** Server id once persisted; null while staging a brand-new form locally. */
    id: number | null;
    name: string;
    questions: IntakeQuestionDraft[];
};

const MY_HOURS_SEGMENTS = ['my-hours'];

export default function ConfigurationSection({
    signOut,
    business,
    service,
    businessLoadError,
    onReloadBusiness,
    isLoadingBusiness,
    isActive,
    modalSegments,
    onNavigate,
    isOwner,
}: {
    signOut: () => void;
    business: BusinessDetails | null;
    service: BusinessService | null;
    businessLoadError: string | null;
    onReloadBusiness: () => Promise<void> | void;
    isLoadingBusiness: boolean;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    whatsappOnboardingConfigId: string;
    isOwner: boolean;
}) {
    // Draft states - only used when modals are open
    const [profileDraft, setProfileDraft] = useState<ProfileState>(emptyProfile);
    const [bookingConfigDraft, setBookingConfigDraft] = useState<BookingConfigState>(emptyBookingConfig);
    const [notificationConfigDraft, setNotificationConfigDraft] =
        useState<NotificationConfigState>(emptyNotificationConfig);
    const [bookingRulesSaving, setBookingRulesSaving] = useState(false);
    const [openingHoursDraft, setOpeningHoursDraft] = useState<OpeningHoursDraft>(emptyDraft());
    const [openingHoursSaving, setOpeningHoursSaving] = useState(false);
    const [notificationsSaving, setNotificationsSaving] = useState(false);
    const [calendarSaving, setCalendarSaving] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [phoneEditWarningOpen, setPhoneEditWarningOpen] = useState(false);
    const [phoneEditModalOpen, setPhoneEditModalOpen] = useState(false);
    const [myHoursSaving, setMyHoursSaving] = useState(false);

    const [intakeDrafts, setIntakeDrafts] = useState<IntakeDraft[]>([]);
    // null = unselected (desktop falls back to first form, mobile shows master list); number = explicit selection.
    const [selectedIntakeIndex, setSelectedIntakeIndex] = useState<number | null>(null);
    const [intakeSaving, setIntakeSaving] = useState(false);
    const [intakeRemoveTarget, setIntakeRemoveTarget] = useState<IntakeDraft | null>(null);

    const { showToast } = useToast();

    // Modal routes for each section
    const {
        isOpen: isProfileModalOpen,
        open: openProfileModal,
        close: closeProfileModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'profile',
    });

    const {
        isOpen: isCalendarModalOpen,
        open: openCalendarModal,
        close: closeCalendarModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'calendar',
    });

    const {
        isOpen: isBookingRulesModalOpen,
        open: openBookingRulesModal,
        close: closeBookingRulesModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'booking-rules',
    });

    const {
        isOpen: isNotificationsModalOpen,
        open: openNotificationsModal,
        close: closeNotificationsModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'notifications',
    });

    const {
        isOpen: isOpeningHoursModalOpen,
        open: openOpeningHoursModal,
        close: closeOpeningHoursModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'opening-hours',
    });

    const {
        isOpen: isIntakeModalOpen,
        open: openIntakeModal,
        close: closeIntakeModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'intake',
    });

    const {
        isOpen: isMyHoursModalOpen,
        open: openMyHoursModal,
        close: closeMyHoursModal,
    } = useSingleModalRoute({
        isActive,
        ready: business !== null,
        segments: modalSegments,
        onNavigate,
        modalKey: 'my-hours',
    });

    const {
        isOpen: isMyHoursEditorOpen,
        open: openMyHoursEditor,
        close: closeMyHoursEditor,
    } = useSingleModalRoute({
        isActive: isMyHoursModalOpen,
        ready: business !== null,
        segments: modalSegments.slice(1),
        onNavigate,
        modalKey: 'opening-hours',
        baseSegments: MY_HOURS_SEGMENTS,
    });

    useEffect(() => {
        if (!isActive) return;
        if (modalSegments.length === 0) return;

        if (!isOwner) {
            const isMyHoursRoute =
                modalSegments[0] === 'my-hours' &&
                (modalSegments.length === 1 || (modalSegments.length === 2 && modalSegments[1] === 'opening-hours'));
            if (!isMyHoursRoute) onNavigate([], true);
            return;
        }

        const validModalKeys = [
            'profile',
            'calendar',
            'assistant',
            'booking-rules',
            'notifications',
            'opening-hours',
            'intake',
            'whatsapp',
            'plan',
        ];
        const [firstSegment] = modalSegments;

        if (modalSegments.length === 1 && !validModalKeys.includes(firstSegment)) {
            onNavigate([], true);
            return;
        }

        if (modalSegments.length > 1) {
            onNavigate([], true);
            return;
        }
    }, [isActive, isOwner, modalSegments, onNavigate]);

    // Initialize draft states when modals open
    useEffect(() => {
        if (isProfileModalOpen && business) {
            setProfileDraft({
                name: business.name ?? '',
                slug: business.slug,
                description: business.description ?? '',
                ownerPhone: business.owner.phoneNumber,
                ownerEmail: business.owner.email,
                botEnabled: business.bot.enabled,
                calendarUrl: business.calendarUrl ?? '',
            });
        }
    }, [isProfileModalOpen, business]);

    useEffect(() => {
        if (isCalendarModalOpen && business) {
            setProfileDraft((prev) => ({
                ...prev,
                ownerEmail: business.owner.email,
                calendarUrl: business.calendarUrl ?? '',
            }));
        }
    }, [isCalendarModalOpen, business]);

    useEffect(() => {
        if (isBookingRulesModalOpen && business) {
            setBookingConfigDraft({
                minHoursNotice: business.booking.minHoursNotice,
                maxDaysAhead: business.booking.maxDaysAhead,
                maxDurationInMinutes: business.booking.maxDurationInMinutes,
                maxConcurrent: business.booking.maxConcurrent,
                checkInEnabled: business.checkInEnabled,
                bookingPageEnabled: business.bookingPageEnabled ?? true,
            });
        }
    }, [isBookingRulesModalOpen, business]);

    useEffect(() => {
        if (isNotificationsModalOpen && business) {
            setNotificationConfigDraft({
                remindersEnabled: business.bookingReminders.enabled,
                reminders: business.bookingReminders.items.map(reminderToDraft),
                confirmationsEnabled: business.bookingConfirmation !== null,
                confirmationDaysBefore:
                    business.bookingConfirmation?.daysBefore ?? emptyNotificationConfig.confirmationDaysBefore,
                confirmationTime: business.bookingConfirmation
                    ? formatPlainTime(business.bookingConfirmation.time)
                    : emptyNotificationConfig.confirmationTime,
            });
        }
    }, [isNotificationsModalOpen, business]);

    useEffect(() => {
        if (isOpeningHoursModalOpen) {
            if (business) {
                setOpeningHoursDraft(toDraft(business.openingHours));
            }
        }
    }, [isOpeningHoursModalOpen, business]);

    useEffect(() => {
        if (isIntakeModalOpen && business) {
            const drafts: IntakeDraft[] = (business.intakes ?? []).map((intake) => ({
                id: intake.id,
                name: intake.name,
                questions: intake.questions.map((q) => ({
                    id: q.id,
                    questionText: q.questionText,
                    type: q.type,
                    options: q.options ?? [],
                    required: q.required,
                })),
            }));
            setIntakeDrafts(drafts);
            setSelectedIntakeIndex(null);
            setIntakeRemoveTarget(null);
        }
    }, [isIntakeModalOpen, business]);

    const updateIntakeDraft = (index: number, updates: Partial<IntakeDraft>) => {
        setIntakeDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...updates } : d)));
    };

    const addNewIntake = () => {
        gtagEvent('config_intake_add_click');
        const draft: IntakeDraft = { id: null, name: 'Novo formulário', questions: [] };
        setIntakeDrafts((prev) => [...prev, draft]);
        setSelectedIntakeIndex(intakeDrafts.length);
    };

    const handleSaveIntakes = async () => {
        if (!service) return;
        gtagEvent('config_intake_save_click');

        for (const draft of intakeDrafts) {
            if (!draft.name.trim()) {
                showToast('Todos os formulários devem ter um nome.', 'error', 'modal');
                return;
            }
            for (const question of draft.questions) {
                if (!question.questionText.trim()) {
                    showToast('Todas as perguntas devem ter um texto.', 'error', 'modal');
                    return;
                }
                if (question.type === 'enum' && question.options.filter((o) => o.trim()).length === 0) {
                    showToast('Perguntas de escolha devem ter pelo menos uma opção.', 'error', 'modal');
                    return;
                }
            }
        }

        setIntakeSaving(true);
        try {
            for (const draft of intakeDrafts) {
                const payload = {
                    name: draft.name.trim(),
                    questions: draft.questions.map((q) => ({
                        id: q.id,
                        questionText: q.questionText.trim(),
                        type: q.type,
                        options: q.type === 'enum' ? q.options.filter((o) => o.trim()) : null,
                        required: q.required,
                    })),
                };
                if (draft.id === null) {
                    await service.createIntake(payload);
                } else {
                    await service.updateIntake(draft.id, payload);
                }
            }

            if (onReloadBusiness) await onReloadBusiness();
            showToast('Formulários salvos com sucesso!', 'success', 'app');
            closeIntakeModal();
        } catch (error) {
            console.error('Failed to save intakes', error);
            if (error instanceof DuplicateIntakeNameError) {
                showToast('Já existe um formulário com esse nome.', 'error', 'modal');
            } else {
                showToast('Não foi possível salvar os formulários.', 'error', 'modal');
            }
        } finally {
            setIntakeSaving(false);
        }
    };

    const handleRequestRemoveIntake = (draft: IntakeDraft) => {
        setIntakeRemoveTarget(draft);
    };

    const handleConfirmRemoveIntake = async () => {
        if (!intakeRemoveTarget || !service) return;
        gtagEvent('config_intake_delete_click', { item_id: intakeRemoveTarget.id });

        const removeFromList = () => {
            const targetIndex =
                intakeRemoveTarget.id === null
                    ? intakeDrafts.findIndex((d) => d === intakeRemoveTarget)
                    : intakeDrafts.findIndex((d) => d.id === intakeRemoveTarget.id);
            const nextDrafts = intakeDrafts.filter((_, i) => i !== targetIndex);
            setIntakeDrafts(nextDrafts);
            setSelectedIntakeIndex(nextDrafts.length === 0 ? null : Math.max(0, targetIndex - 1));
        };

        if (intakeRemoveTarget.id === null) {
            removeFromList();
            setIntakeRemoveTarget(null);
            return;
        }

        try {
            await service.deleteIntake(intakeRemoveTarget.id);
            if (onReloadBusiness) await onReloadBusiness();
            removeFromList();
            showToast('Formulário removido.', 'success', 'modal');
        } catch (error) {
            console.error('Failed to delete intake', error);
            showToast('Não foi possível remover o formulário.', 'error', 'modal');
        } finally {
            setIntakeRemoveTarget(null);
        }
    };

    const addIntakeQuestion = (intakeIndex: number) => {
        setIntakeDrafts((prev) =>
            prev.map((d, i) =>
                i === intakeIndex
                    ? {
                          ...d,
                          questions: [
                              ...d.questions,
                              { id: null, questionText: '', type: 'text', options: [], required: true },
                          ],
                      }
                    : d,
            ),
        );
    };

    const removeIntakeQuestion = (intakeIndex: number, questionIndex: number) => {
        setIntakeDrafts((prev) =>
            prev.map((d, i) =>
                i === intakeIndex ? { ...d, questions: d.questions.filter((_, qi) => qi !== questionIndex) } : d,
            ),
        );
    };

    const moveIntakeQuestion = (intakeIndex: number, questionIndex: number, direction: 'up' | 'down') => {
        setIntakeDrafts((prev) =>
            prev.map((d, i) => {
                if (i !== intakeIndex) return d;
                const next = [...d.questions];
                const target = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
                if (target < 0 || target >= next.length) return d;
                [next[questionIndex], next[target]] = [next[target], next[questionIndex]];
                return { ...d, questions: next };
            }),
        );
    };

    const updateIntakeQuestion = (
        intakeIndex: number,
        questionIndex: number,
        updates: Partial<IntakeQuestionDraft>,
    ) => {
        setIntakeDrafts((prev) =>
            prev.map((d, i) =>
                i === intakeIndex
                    ? {
                          ...d,
                          questions: d.questions.map((q, qi) => (qi === questionIndex ? { ...q, ...updates } : q)),
                      }
                    : d,
            ),
        );
    };

    const getIntakeSummary = (): string[] => {
        if (!business) return [];
        const intakes = business.intakes ?? [];
        if (intakes.length === 0) return ['Nenhum formulário cadastrado'];
        return intakes.map((intake) => {
            const count = intake.questions.length;
            return `${intake.name} · ${count} pergunta${count === 1 ? '' : 's'}`;
        });
    };

    const getServicesBoundToIntake = (intakeId: number | null): string[] => {
        if (intakeId === null || !business) return [];
        return business.services.filter((s) => s.intakeId === intakeId).map((s) => s.name);
    };

    const isLoading = useMemo(() => business === null, [business]);

    const handleProfileChange = (field: string, value: any) => {
        setProfileDraft((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveProfile = async () => {
        if (!service) return;
        gtagEvent('config_profile_save_click');

        const name = profileDraft.name.trim();
        if (!name) {
            showToast('O nome do estabelecimento é obrigatório.', 'error', 'modal');
            return;
        }
        if (name.length > 80) {
            showToast('O nome não pode ter mais de 80 caracteres.', 'error', 'modal');
            return;
        }

        const description = profileDraft.description.trim();
        if (description.length > 300) {
            showToast('A descrição não pode ter mais de 300 caracteres.', 'error', 'modal');
            return;
        }

        const slug = profileDraft.slug.trim();
        if (!slug) {
            showToast('O link de agendamento é obrigatório.', 'error', 'modal');
            return;
        }
        if (slug.length < 3) {
            showToast('O link deve ter pelo menos 3 caracteres.', 'error', 'modal');
            return;
        }
        if (!/^[a-z0-9-]+$/.test(slug)) {
            showToast('O link deve conter apenas letras minúsculas, números e hífens.', 'error', 'modal');
            return;
        }

        setProfileSaving(true);
        try {
            const result = await service.setProfile({
                name,
                description,
                slug,
            });
            if (result.error === 'slug_taken') {
                showToast('Este link já está em uso. Escolha outro.', 'error', 'modal');
                return;
            }
            if (onReloadBusiness) {
                await onReloadBusiness();
            }
            showToast('Perfil do estabelecimento salvo com sucesso!', 'success', 'app');
            closeProfileModal();
        } catch (error) {
            console.error('Failed to save profile', error);
            showToast('Não foi possível salvar o perfil do estabelecimento.', 'error', 'modal');
        } finally {
            setProfileSaving(false);
        }
    };

    const handleOwnerPhoneChanged = async () => {
        if (onReloadBusiness) await onReloadBusiness();
        showToast('Número de WhatsApp atualizado.', 'success', 'modal');
    };

    const toggleOwnerCalendarEmail = (enabled: boolean) => {
        setProfileDraft((prev) => ({
            ...prev,
            ownerEmail: enabled ? (prev.ownerEmail ?? '') : null,
        }));
    };

    const handleSaveCalendar = async () => {
        if (!service) return;
        gtagEvent('config_calendar_save_click');

        const emailValidation = validateEmail(profileDraft.ownerEmail);
        if (!emailValidation.isValid) {
            showToast(emailValidation.errorMessage, 'error', 'modal');
            return;
        }

        setCalendarSaving(true);
        try {
            await service.setOwnerEmail({
                email: emailValidation.trimmedEmail,
            });
            if (onReloadBusiness) {
                await onReloadBusiness();
            }
            showToast('Configurações do Google Agenda salvas com sucesso!', 'success', 'app');
            closeCalendarModal();
        } catch (error) {
            console.error('Failed to save calendar settings', error);
            showToast('Não foi possível salvar as configurações do Google Agenda.', 'error', 'modal');
        } finally {
            setCalendarSaving(false);
        }
    };

    const handleBookingChange = (field: keyof BookingConfigState, value: any) => {
        setBookingConfigDraft((prev) => ({ ...prev, [field]: value }));
    };

    const toggleBookingField = (field: keyof BookingConfigState, enabled: boolean, fallback: number) => {
        setBookingConfigDraft((prev) => ({
            ...prev,
            [field]: enabled ? (prev[field] ?? fallback) : null,
        }));
    };

    const handleNotificationChange = (field: keyof NotificationConfigState, value: any) => {
        setNotificationConfigDraft((prev) => ({ ...prev, [field]: value }));
    };

    const addReminder = () => {
        setNotificationConfigDraft((prev) => {
            if (prev.reminders.length >= MAX_BOOKING_REMINDERS) return prev;
            gtagEvent('config_reminder_add_click', { value: prev.reminders.length });
            return { ...prev, reminders: [...prev.reminders, newReminderDraft()] };
        });
    };

    const removeReminder = (index: number) => {
        gtagEvent('config_reminder_remove_click', { value: index });
        setNotificationConfigDraft((prev) => ({
            ...prev,
            reminders: prev.reminders.filter((_, i) => i !== index),
        }));
    };

    const setReminderKind = (index: number, kind: BookingReminderDraft['kind']) => {
        setNotificationConfigDraft((prev) => ({
            ...prev,
            reminders: prev.reminders.map((reminder, i) => {
                if (i !== index) return reminder;
                if (kind === 'minutesBefore') return { kind: 'minutesBefore', minutes: '60' };
                return { kind: 'daysBefore', daysBefore: '1', time: '09:00' };
            }),
        }));
    };

    const updateReminderField = (
        index: number,
        updates: Partial<{ minutes: string; daysBefore: string; time: string }>,
    ) => {
        setNotificationConfigDraft((prev) => ({
            ...prev,
            reminders: prev.reminders.map((reminder, i) => {
                if (i !== index) return reminder;
                if (reminder.kind === 'minutesBefore' && updates.minutes !== undefined) {
                    return { ...reminder, minutes: updates.minutes };
                }
                if (reminder.kind === 'daysBefore') {
                    return {
                        ...reminder,
                        daysBefore: updates.daysBefore ?? reminder.daysBefore,
                        time: updates.time ?? reminder.time,
                    };
                }
                return reminder;
            }),
        }));
    };

    const handleSaveNotifications = async () => {
        if (!service) return;
        gtagEvent('config_notifications_save_click');

        const reminders: BookingReminder[] = [];
        for (const draft of notificationConfigDraft.reminders) {
            const result = draftToReminder(draft);
            if ('error' in result) {
                showToast(result.error, 'error', 'modal');
                return;
            }
            reminders.push(result);
        }
        if (reminders.length > MAX_BOOKING_REMINDERS) {
            showToast(`É permitido no máximo ${MAX_BOOKING_REMINDERS} lembretes.`, 'error', 'modal');
            return;
        }
        const seen = new Set<string>();
        for (const reminder of reminders) {
            const key = reminderUniqueKey(reminder);
            if (seen.has(key)) {
                showToast('Não é permitido repetir o mesmo lembrete.', 'error', 'modal');
                return;
            }
            seen.add(key);
        }

        if (notificationConfigDraft.confirmationsEnabled) {
            const daysBefore = notificationConfigDraft.confirmationDaysBefore;
            if (isNaN(daysBefore) || daysBefore <= 0 || !Number.isInteger(daysBefore)) {
                showToast('Os dias antes da confirmação devem ser um número inteiro maior que zero.', 'error', 'modal');
                return;
            }

            const timeMatch = notificationConfigDraft.confirmationTime.match(/^(\d{2}):(\d{2})$/);
            if (!timeMatch) {
                showToast('O horário de confirmação deve estar no formato HH:MM.', 'error', 'modal');
                return;
            }
            const hours = parseInt(timeMatch[1], 10);
            const minutes = parseInt(timeMatch[2], 10);
            if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
                showToast('O horário de confirmação deve ser válido (00:00 a 23:59).', 'error', 'modal');
                return;
            }
        }

        setNotificationsSaving(true);
        try {
            const timeMatch = notificationConfigDraft.confirmationsEnabled
                ? notificationConfigDraft.confirmationTime.match(/^(\d{2}):(\d{2})$/)
                : null;

            await service.setNotifications({
                bookingReminders: {
                    enabled: notificationConfigDraft.remindersEnabled,
                    items: reminders,
                },
                bookingConfirmation:
                    notificationConfigDraft.confirmationsEnabled && timeMatch
                        ? {
                              daysBefore: notificationConfigDraft.confirmationDaysBefore,
                              time: {
                                  hour: parseInt(timeMatch[1], 10),
                                  minute: parseInt(timeMatch[2], 10),
                              },
                          }
                        : null,
            });
            if (onReloadBusiness) {
                await onReloadBusiness();
            }
            showToast('Notificações salvas com sucesso!', 'success', 'app');
            closeNotificationsModal();
        } catch (error) {
            console.error('Failed to save notifications', error);
            showToast('Não foi possível salvar as notificações.', 'error', 'modal');
        } finally {
            setNotificationsSaving(false);
        }
    };

    const handleSaveBookingRules = async () => {
        if (!service) return;
        gtagEvent('config_booking_rules_save_click');

        // Validação
        if (bookingConfigDraft.minHoursNotice !== null) {
            const value = bookingConfigDraft.minHoursNotice;
            if (isNaN(value) || value <= 0 || !Number.isInteger(value)) {
                showToast('A antecedência mínima deve ser um número inteiro maior que zero.', 'error', 'modal');
                return;
            }
        }
        if (bookingConfigDraft.maxDaysAhead !== null) {
            const value = bookingConfigDraft.maxDaysAhead;
            if (isNaN(value) || value <= 0 || !Number.isInteger(value)) {
                showToast('O máximo de dias no futuro deve ser um número inteiro maior que zero.', 'error', 'modal');
                return;
            }
        }
        if (bookingConfigDraft.maxDurationInMinutes !== null) {
            const value = bookingConfigDraft.maxDurationInMinutes;
            if (isNaN(value) || value <= 0 || !Number.isInteger(value)) {
                showToast('A duração máxima deve ser um número inteiro maior que zero.', 'error', 'modal');
                return;
            }
        }
        if (bookingConfigDraft.maxConcurrent !== null) {
            const value = bookingConfigDraft.maxConcurrent;
            if (isNaN(value) || value <= 0 || !Number.isInteger(value)) {
                showToast(
                    'O limite de agendamentos por cliente deve ser um número inteiro maior que zero.',
                    'error',
                    'modal',
                );
                return;
            }
        }

        setBookingRulesSaving(true);
        try {
            await service.setBookingRules({
                minHoursNotice: bookingConfigDraft.minHoursNotice,
                maxDaysAhead: bookingConfigDraft.maxDaysAhead,
                maxDurationInMinutes: bookingConfigDraft.maxDurationInMinutes,
                maxConcurrent: bookingConfigDraft.maxConcurrent,
                checkInEnabled: bookingConfigDraft.checkInEnabled,
                ...(business?.bookingPageEnabled !== undefined && {
                    bookingPageEnabled: bookingConfigDraft.bookingPageEnabled,
                }),
            });
            if (onReloadBusiness) {
                await onReloadBusiness();
            }
            showToast('Regras de agendamento salvas com sucesso!', 'success', 'app');
            closeBookingRulesModal();
        } catch (error) {
            console.error('Failed to save booking rules', error);
            showToast('Não foi possível salvar as regras de agendamento.', 'error', 'modal');
        } finally {
            setBookingRulesSaving(false);
        }
    };

    const handleSaveOpeningHours = async () => {
        if (!service) return;
        gtagEvent('config_opening_hours_save_click');
        setOpeningHoursSaving(true);
        try {
            await service.setOpeningHours(fromDraft(openingHoursDraft));
            if (onReloadBusiness) {
                await onReloadBusiness();
            }
            showToast('Horário de funcionamento salvo com sucesso!', 'success', 'app');
            closeOpeningHoursModal();
        } catch (error) {
            console.error('Failed to save opening hours', error);
            showToast('Não foi possível salvar o horário de funcionamento.', 'error', 'modal');
        } finally {
            setOpeningHoursSaving(false);
        }
    };

    // Helper functions to generate summaries - use business object directly
    const getProfileSummary = () => {
        if (!business) return ['Nenhuma informação configurada'];
        const items: string[] = [];
        if (business.name) items.push(`Nome: ${business.name}`);
        const link = `${business.slug}.book-organizabot.com`;
        if (!hasPaidAccess(business)) items.push(`Link: ${link} (indisponível)`);
        else items.push(business.bookingPageEnabled === false ? `Link: ${link} (desativado)` : `Link: ${link}`);
        return items.length > 0 ? items : ['Nenhuma informação configurada'];
    };

    const getCalendarSummary = () => {
        if (!business) return ['Nenhum email configurado'];
        if (business.owner.email) {
            return [`Email configurado: ${business.owner.email}`];
        }
        return ['Nenhum email configurado'];
    };

    const getBookingRulesSummary = () => {
        if (!business) return ['Nenhuma regra configurada'];
        const items: string[] = [];
        if (business.booking.minHoursNotice !== null)
            items.push(`Antecedência mínima: ${business.booking.minHoursNotice}h`);
        if (business.booking.maxDaysAhead !== null) items.push(`Máximo de dias: ${business.booking.maxDaysAhead}`);
        if (business.booking.maxDurationInMinutes !== null)
            items.push(`Duração máxima: ${business.booking.maxDurationInMinutes}min`);
        if (business.booking.maxConcurrent !== null)
            items.push(`Limite por cliente: ${business.booking.maxConcurrent}`);
        if (business.checkInEnabled) items.push('Check-in habilitado');
        if (business.bookingPageEnabled === false) items.push('Página de agendamento online desativada');
        return items.length > 0 ? items : ['Nenhuma regra configurada'];
    };

    const getNotificationsSummary = () => {
        if (!business) return ['Nenhuma notificação configurada'];
        const items: string[] = [];
        if (business.bookingReminders.enabled) {
            for (const reminder of business.bookingReminders.items) {
                if (reminder.kind === 'minutesBefore') {
                    items.push(`Lembrete: ${reminder.minutes}min antes`);
                } else {
                    const timeStr = formatPlainTime(reminder.time);
                    if (reminder.daysBefore === 0) {
                        items.push(`Lembrete: no dia às ${timeStr}`);
                    } else {
                        items.push(
                            `Lembrete: ${reminder.daysBefore} dia${reminder.daysBefore > 1 ? 's' : ''} antes às ${timeStr}`,
                        );
                    }
                }
            }
        }
        if (business.bookingConfirmation !== null) {
            const timeStr = formatPlainTime(business.bookingConfirmation.time);
            items.push(
                `Confirmação: ${business.bookingConfirmation.daysBefore} dia${business.bookingConfirmation.daysBefore > 1 ? 's' : ''} antes às ${timeStr}`,
            );
        }
        return items.length > 0 ? items : ['Nenhuma notificação configurada'];
    };

    const getOpeningHoursSummary = () => {
        if (!business) return ['Nenhum horário configurado'];
        return summarizeOpeningHours(toDraft(business.openingHours), { closedLabel: 'Fechado' });
    };

    if (!isOwner) {
        const myWorker = business?.workers.find((worker) => worker.id === business.workerId) ?? null;

        const handleToggleMyHours = async (enabled: boolean) => {
            if (!service || !myWorker) return;
            gtagEvent('config_my_hours_toggle_click', { action: enabled ? 'on' : 'off' });
            setMyHoursSaving(true);
            try {
                await service.setWorkerOpeningHoursEnabled({ workerId: myWorker.id, enabled });
                await onReloadBusiness();
            } catch (error) {
                console.error('Failed to save own opening hours setting', error);
                showToast('Não foi possível salvar a configuração.', 'error', 'modal');
            } finally {
                setMyHoursSaving(false);
            }
        };

        const handleSaveMyHours = async (openingHours: PlainOpeningHours) => {
            if (!service || !myWorker) return;
            gtagEvent('config_my_hours_save_click', {
                value: WEEKDAYS.filter((weekday) => openingHours[weekday].length > 0).length,
            });
            setMyHoursSaving(true);
            try {
                await service.setWorkerOpeningHours({ workerId: myWorker.id, openingHours });
                await onReloadBusiness();
                closeMyHoursEditor();
                showToast('Horário salvo com sucesso!', 'success', 'modal');
            } catch (error) {
                console.error('Failed to save own opening hours', error);
                showToast('Não foi possível salvar o horário.', 'error', 'modal');
            } finally {
                setMyHoursSaving(false);
            }
        };

        return (
            <Container>
                <Header>
                    <Title>Configurações</Title>
                </Header>
                {myWorker && (
                    <ConfigList>
                        <ConfigCard
                            data-testid="config-my-hours"
                            onClick={() => {
                                gtagEvent('config_my_hours_click');
                                openMyHoursModal();
                            }}
                        >
                            <ConfigInfo>
                                <ConfigTitle>Meu horário</ConfigTitle>
                                <ConfigSummary>
                                    {(myWorker.openingHours === null
                                        ? ['Segue o horário do estabelecimento']
                                        : summarizeWorkerOpeningHours(myWorker.openingHours)
                                    ).map((item) => (
                                        <SummaryItem key={item}>{item}</SummaryItem>
                                    ))}
                                </ConfigSummary>
                            </ConfigInfo>
                        </ConfigCard>
                    </ConfigList>
                )}
                {myWorker && business && (
                    <>
                        <Modal
                            isOpen={isMyHoursModalOpen}
                            onClose={closeMyHoursModal}
                            title="Meu horário"
                            data-testid="my-hours-modal"
                            footer={
                                <Button data-testid="modal-cancel" onClick={closeMyHoursModal} disabled={myHoursSaving}>
                                    Fechar
                                </Button>
                            }
                        >
                            <FormGroup>
                                <InputGroup>
                                    <WorkerOpeningHoursField
                                        openingHours={myWorker.openingHours}
                                        onToggle={handleToggleMyHours}
                                        onConfigure={() => {
                                            gtagEvent('config_my_hours_configure_click');
                                            openMyHoursEditor();
                                        }}
                                        disabled={myHoursSaving}
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Modal>
                        <WorkerOpeningHoursModal
                            isOpen={isMyHoursEditorOpen}
                            title="Meu horário"
                            workerOpeningHours={myWorker.openingHours}
                            businessOpeningHours={business.openingHours}
                            isSaving={myHoursSaving}
                            onClose={() => {
                                gtagEvent('config_my_hours_cancel_click');
                                closeMyHoursEditor();
                            }}
                            onCopyBusinessHours={() => gtagEvent('config_my_hours_copy_click')}
                            onSave={handleSaveMyHours}
                        />
                    </>
                )}
                <div style={{ paddingTop: '24px' }}>
                    <Button
                        type="button"
                        danger
                        onClick={() => {
                            gtagEvent('logout_click');
                            signOut();
                        }}
                        style={{ width: 'fit-content' }}
                        data-testid="sign-out-btn"
                    >
                        Encerrar sessão
                    </Button>
                </div>
            </Container>
        );
    }

    if (businessLoadError) {
        return (
            <Container>
                <Header>
                    <Title>Configurações</Title>
                </Header>
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                    <Button onClick={onReloadBusiness} disabled={isLoadingBusiness}>
                        {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                    </Button>
                </div>
            </Container>
        );
    }

    if (isLoading) {
        return (
            <Container>
                <Header>
                    <Title>Configurações</Title>
                </Header>
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando configurações...</div>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <Title>Configurações</Title>
            </Header>

            <ConfigList>
                <ConfigCard
                    data-testid="config-profile"
                    onClick={() => {
                        gtagEvent('config_profile_click');
                        openProfileModal();
                    }}
                >
                    <ConfigInfo>
                        <ConfigTitle>Perfil do Estabelecimento</ConfigTitle>
                        <ConfigSummary>
                            {getProfileSummary().map((item, idx) => (
                                <SummaryItem key={idx}>{item}</SummaryItem>
                            ))}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>

                <ConfigCard
                    data-testid="config-calendar"
                    onClick={() => {
                        gtagEvent('config_calendar_click');
                        openCalendarModal();
                    }}
                >
                    <ConfigInfo>
                        <ConfigTitle>Google Agenda</ConfigTitle>
                        <ConfigSummary>
                            {getCalendarSummary().map((item, idx) => (
                                <SummaryItem key={idx}>{item}</SummaryItem>
                            ))}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>

                <ConfigCard
                    data-testid="config-booking-rules"
                    onClick={() => {
                        gtagEvent('config_booking_rules_click');
                        openBookingRulesModal();
                    }}
                >
                    <ConfigInfo>
                        <ConfigTitle>Agendamentos</ConfigTitle>
                        <ConfigSummary>
                            {getBookingRulesSummary().map((item, idx) => (
                                <SummaryItem key={idx}>{item}</SummaryItem>
                            ))}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>

                <ConfigCard
                    data-testid="config-notifications"
                    onClick={() => {
                        gtagEvent('config_notifications_click');
                        openNotificationsModal();
                    }}
                >
                    <ConfigInfo>
                        <ConfigTitle>Notificações</ConfigTitle>
                        <ConfigSummary>
                            {getNotificationsSummary().map((item, idx) => (
                                <SummaryItem key={idx}>{item}</SummaryItem>
                            ))}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>

                <ConfigCard
                    data-testid="config-opening-hours"
                    onClick={() => {
                        gtagEvent('config_opening_hours_click');
                        openOpeningHoursModal();
                    }}
                >
                    <ConfigInfo>
                        <ConfigTitle>Horário de Funcionamento</ConfigTitle>
                        <ConfigSummary>
                            {getOpeningHoursSummary().map((item, idx) => (
                                <SummaryItem key={idx}>{item}</SummaryItem>
                            ))}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>

                {isOwner && (
                    <ConfigCard
                        data-testid="config-intake"
                        onClick={() => {
                            gtagEvent('config_intake_click');
                            openIntakeModal();
                        }}
                    >
                        <ConfigInfo>
                            <ConfigTitle>Formulários</ConfigTitle>
                            <ConfigSummary>
                                {getIntakeSummary().map((item, idx) => (
                                    <SummaryItem key={idx}>{item}</SummaryItem>
                                ))}
                            </ConfigSummary>
                        </ConfigInfo>
                    </ConfigCard>
                )}
            </ConfigList>

            <div
                style={{
                    marginTop: '32px',
                    paddingTop: '24px',
                    borderTop: `1px solid ${vars.dividerColor}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                {business?.whatsapp && business.whatsapp.phoneNumber !== business.owner.phoneNumber && (
                    <InfoCard>
                        <ConfigInfo>
                            <ConfigTitle>Chat</ConfigTitle>
                            <ConfigSummary>
                                <SummaryItem>Acompanhe as conversas</SummaryItem>
                            </ConfigSummary>
                        </ConfigInfo>
                        {business.chatUrl ? (
                            <Button
                                as="a"
                                href={business.chatUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ width: 'fit-content' }}
                                onClick={() => gtagEvent('config_chat_click')}
                            >
                                Abrir chat
                            </Button>
                        ) : (
                            <Button disabled style={{ width: 'fit-content' }}>
                                Abrir chat
                            </Button>
                        )}
                    </InfoCard>
                )}

                {business?.subscription && (
                    <SubscriptionSection
                        business={business}
                        service={service}
                        onReloadBusiness={onReloadBusiness}
                        isActive={isActive}
                        modalSegments={modalSegments}
                        onNavigate={onNavigate}
                    />
                )}
            </div>

            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${vars.dividerColor}` }}>
                <Button
                    type="button"
                    danger
                    onClick={() => {
                        gtagEvent('logout_click');
                        signOut();
                    }}
                    style={{ width: 'fit-content' }}
                    data-testid="sign-out-btn"
                >
                    Encerrar sessão
                </Button>
            </div>

            {/* Profile Modal */}
            <Modal
                isOpen={isProfileModalOpen}
                onClose={closeProfileModal}
                title="Perfil do Estabelecimento"
                data-testid="profile-modal"
                footer={
                    <>
                        <Button
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_profile_cancel_click');
                                closeProfileModal();
                            }}
                            disabled={profileSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="modal-save"
                            primary
                            type="submit"
                            form="profile-form"
                            disabled={profileSaving}
                        >
                            {profileSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="profile-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveProfile();
                    }}
                >
                    <FormGroup>
                        <InputGroup>
                            Nome do Estabelecimento
                            <Input
                                value={profileDraft.name}
                                onChange={(e) => handleProfileChange('name', e.target.value)}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Descrição</LabelRow>
                            <TextArea
                                value={profileDraft.description}
                                onChange={(e) => handleProfileChange('description', e.target.value)}
                                maxLength={300}
                            />
                            <HelperText>{profileDraft.description.length}/300 caracteres</HelperText>
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Link de Agendamento</LabelRow>
                            <Input
                                value={profileDraft.slug}
                                onChange={(e) =>
                                    handleProfileChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                                }
                                placeholder="meu-estabelecimento"
                            />
                            <small style={{ color: '#666', marginTop: '4px' }}>
                                {profileDraft.slug ? (
                                    <>
                                        Seus clientes acessarão:{' '}
                                        <strong>https://{profileDraft.slug}.book-organizabot.com</strong>
                                    </>
                                ) : (
                                    'Link que seus clientes usarão para agendar online.'
                                )}
                            </small>
                            {business && !hasPaidAccess(business) && (
                                <HelperText data-testid="booking-page-unavailable-hint">
                                    Sem uma assinatura ativa, este link não abre para seus clientes.
                                </HelperText>
                            )}
                            {business?.bookingPageEnabled === false && (
                                <HelperText data-testid="booking-page-disabled-hint">
                                    A página de agendamento online está desativada em Regras de Agendamento — este link
                                    não abre para seus clientes.
                                </HelperText>
                            )}
                            {profileDraft.slug && (
                                <Button
                                    type="button"
                                    style={{ width: 'fit-content', marginTop: '8px' }}
                                    onClick={() => {
                                        gtagEvent('config_profile_copy_link_click');
                                        navigator.clipboard.writeText(
                                            `https://${profileDraft.slug}.book-organizabot.com`,
                                        );
                                        showToast('Link copiado!', 'success', 'modal');
                                    }}
                                    data-testid="copy-link-btn"
                                >
                                    Copiar link
                                </Button>
                            )}
                        </InputGroup>
                        <InputGroup>
                            WhatsApp do Estabelecimento
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <Input
                                    value={
                                        profileDraft.ownerPhone
                                            ? formatPhone(stripCountryCode(profileDraft.ownerPhone))
                                            : ''
                                    }
                                    readOnly
                                    disabled
                                    style={{ flex: 1 }}
                                />
                                <EditPhoneButton
                                    type="button"
                                    iconOnly
                                    aria-label="Alterar número"
                                    onClick={() => setPhoneEditWarningOpen(true)}
                                    data-testid="owner-phone-edit-btn"
                                >
                                    <EditIcon />
                                </EditPhoneButton>
                            </div>
                        </InputGroup>
                    </FormGroup>
                </form>
            </Modal>

            {/* Calendar Modal */}
            <Modal
                isOpen={isCalendarModalOpen}
                onClose={closeCalendarModal}
                title="Google Agenda"
                footer={
                    <>
                        <Button
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_calendar_cancel_click');
                                closeCalendarModal();
                            }}
                            disabled={calendarSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="modal-save"
                            primary
                            type="submit"
                            form="calendar-form"
                            disabled={calendarSaving}
                        >
                            {calendarSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="calendar-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveCalendar();
                    }}
                >
                    <FormGroup>
                        <InputGroup>
                            <LabelRow>Agenda do estabelecimento</LabelRow>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '4px 0' }}>
                                <Button
                                    as="a"
                                    href={profileDraft.calendarUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ width: 'fit-content' }}
                                    onClick={() => gtagEvent('config_calendar_open_click')}
                                >
                                    Abrir agenda
                                </Button>
                            </div>
                        </InputGroup>
                        <InputGroup>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={profileDraft.ownerEmail !== null}
                                    onChange={(e) => toggleOwnerCalendarEmail(e.target.checked)}
                                />
                                Acesso a todas as agendas do negócio
                            </CheckboxLabel>
                            {profileDraft.ownerEmail !== null && (
                                <>
                                    <LabelRow>Email</LabelRow>
                                    <Input
                                        type="email"
                                        value={profileDraft.ownerEmail}
                                        onChange={(e) => handleProfileChange('ownerEmail', e.target.value)}
                                        placeholder="Email com permissão total em todas as agendas"
                                    />
                                </>
                            )}
                        </InputGroup>
                    </FormGroup>
                </form>
            </Modal>

            {/* Booking Rules Modal */}
            <Modal
                isOpen={isBookingRulesModalOpen}
                onClose={closeBookingRulesModal}
                title="Agendamentos"
                footer={
                    <>
                        <Button
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_booking_rules_cancel_click');
                                closeBookingRulesModal();
                            }}
                            disabled={bookingRulesSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="modal-save"
                            primary
                            type="submit"
                            form="booking-rules-form"
                            disabled={bookingRulesSaving}
                        >
                            {bookingRulesSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="booking-rules-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveBookingRules();
                    }}
                >
                    <FormGroup>
                        <Grid>
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox
                                        checked={bookingConfigDraft.minHoursNotice !== null}
                                        onChange={(e) => toggleBookingField('minHoursNotice', e.target.checked, 2)}
                                    />
                                    Antecedência Mínima
                                </CheckboxLabel>
                                {bookingConfigDraft.minHoursNotice !== null && (
                                    <>
                                        <LabelRow>Horas</LabelRow>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={bookingConfigDraft.minHoursNotice}
                                            onChange={(e) =>
                                                handleBookingChange('minHoursNotice', Number(e.target.value))
                                            }
                                        />
                                    </>
                                )}
                            </InputGroup>
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox
                                        checked={bookingConfigDraft.maxDaysAhead !== null}
                                        onChange={(e) => toggleBookingField('maxDaysAhead', e.target.checked, 30)}
                                    />
                                    Máximo de dias no futuro
                                </CheckboxLabel>
                                {bookingConfigDraft.maxDaysAhead !== null && (
                                    <>
                                        <LabelRow>Dias</LabelRow>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={bookingConfigDraft.maxDaysAhead}
                                            onChange={(e) =>
                                                handleBookingChange('maxDaysAhead', Number(e.target.value))
                                            }
                                        />
                                    </>
                                )}
                            </InputGroup>
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox
                                        checked={bookingConfigDraft.maxDurationInMinutes !== null}
                                        onChange={(e) =>
                                            toggleBookingField('maxDurationInMinutes', e.target.checked, 120)
                                        }
                                    />
                                    Duração Máxima
                                </CheckboxLabel>
                                {bookingConfigDraft.maxDurationInMinutes !== null && (
                                    <>
                                        <LabelRow>Minutos</LabelRow>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={bookingConfigDraft.maxDurationInMinutes}
                                            onChange={(e) =>
                                                handleBookingChange('maxDurationInMinutes', Number(e.target.value))
                                            }
                                        />
                                    </>
                                )}
                            </InputGroup>
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox
                                        checked={bookingConfigDraft.maxConcurrent !== null}
                                        onChange={(e) => toggleBookingField('maxConcurrent', e.target.checked, 2)}
                                    />
                                    Limite de agendamentos por cliente
                                </CheckboxLabel>
                                {bookingConfigDraft.maxConcurrent !== null && (
                                    <>
                                        <LabelRow>Máximo por cliente</LabelRow>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={bookingConfigDraft.maxConcurrent}
                                            onChange={(e) =>
                                                handleBookingChange('maxConcurrent', Number(e.target.value))
                                            }
                                        />
                                    </>
                                )}
                            </InputGroup>
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox
                                        checked={bookingConfigDraft.checkInEnabled}
                                        onChange={(e) => handleBookingChange('checkInEnabled', e.target.checked)}
                                    />
                                    Habilitar check-in de agendamentos
                                </CheckboxLabel>
                            </InputGroup>
                            {business?.bookingPageEnabled !== undefined && (
                                <InputGroup>
                                    <CheckboxLabel>
                                        <Checkbox
                                            checked={bookingConfigDraft.bookingPageEnabled}
                                            onChange={(e) =>
                                                handleBookingChange('bookingPageEnabled', e.target.checked)
                                            }
                                            disabled={business.whatsapp === null}
                                            data-testid="booking-page-enabled-checkbox"
                                        />
                                        Página de agendamento online
                                    </CheckboxLabel>
                                    <HelperText>
                                        {business.whatsapp === null
                                            ? 'Conecte seu WhatsApp para receber agendamentos somente por lá.'
                                            : 'Desmarque para receber agendamentos somente pelo WhatsApp: a página fica indisponível e a assistente para de enviar o link.'}
                                    </HelperText>
                                </InputGroup>
                            )}
                        </Grid>
                    </FormGroup>
                </form>
            </Modal>

            {/* Notifications Modal */}
            <Modal
                isOpen={isNotificationsModalOpen}
                onClose={closeNotificationsModal}
                title="Notificações"
                data-testid="notifications-modal"
                footer={
                    <>
                        <Button
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_notifications_cancel_click');
                                closeNotificationsModal();
                            }}
                            disabled={notificationsSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="modal-save"
                            primary
                            type="submit"
                            form="notifications-form"
                            disabled={notificationsSaving}
                        >
                            {notificationsSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="notifications-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveNotifications();
                    }}
                >
                    <FormGroup>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={notificationConfigDraft.remindersEnabled}
                                    onChange={(e) => handleNotificationChange('remindersEnabled', e.target.checked)}
                                />
                                Lembrete de agendamento
                            </CheckboxLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {notificationConfigDraft.reminders.length === 0 && (
                                    <HelperText>
                                        Adicione até {MAX_BOOKING_REMINDERS} lembretes para o cliente.
                                    </HelperText>
                                )}
                                {notificationConfigDraft.reminders.map((reminder, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '10px',
                                            padding: '12px',
                                            border: `1px solid ${vars.dividerColor}`,
                                            borderRadius: '8px',
                                        }}
                                        data-testid={`reminder-row-${index}`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                Lembrete {index + 1}
                                            </span>
                                            <Button
                                                type="button"
                                                iconOnly
                                                danger
                                                onClick={() => removeReminder(index)}
                                                disabled={notificationsSaving}
                                                aria-label="Remover"
                                                data-testid={`reminder-remove-${index}`}
                                            >
                                                －
                                            </Button>
                                        </div>
                                        <InputGroup>
                                            <LabelRow>Tipo</LabelRow>
                                            <Select
                                                value={reminder.kind}
                                                onChange={(e) =>
                                                    setReminderKind(
                                                        index,
                                                        e.target.value as BookingReminderDraft['kind'],
                                                    )
                                                }
                                                disabled={notificationsSaving}
                                                data-testid={`reminder-kind-${index}`}
                                            >
                                                <option value="minutesBefore">Minutos antes</option>
                                                <option value="daysBefore">Dias antes em horário</option>
                                            </Select>
                                        </InputGroup>
                                        {reminder.kind === 'minutesBefore' ? (
                                            <InputGroup>
                                                <LabelRow>Minutos antes</LabelRow>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={reminder.minutes}
                                                    onChange={(e) =>
                                                        updateReminderField(index, { minutes: e.target.value })
                                                    }
                                                    disabled={notificationsSaving}
                                                    data-testid={`reminder-minutes-${index}`}
                                                />
                                            </InputGroup>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '12px' }}>
                                                <InputGroup style={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <LabelRow>Dias antes</LabelRow>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        value={reminder.daysBefore}
                                                        onChange={(e) =>
                                                            updateReminderField(index, { daysBefore: e.target.value })
                                                        }
                                                        disabled={notificationsSaving}
                                                        data-testid={`reminder-days-${index}`}
                                                    />
                                                </InputGroup>
                                                <InputGroup style={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <LabelRow>Horário</LabelRow>
                                                    <Input
                                                        type="time"
                                                        value={reminder.time}
                                                        onChange={(e) =>
                                                            updateReminderField(index, { time: e.target.value })
                                                        }
                                                        disabled={notificationsSaving}
                                                        data-testid={`reminder-time-${index}`}
                                                    />
                                                </InputGroup>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    disabled={
                                        notificationsSaving ||
                                        notificationConfigDraft.reminders.length >= MAX_BOOKING_REMINDERS
                                    }
                                    onClick={addReminder}
                                    style={{ alignSelf: 'flex-start' }}
                                    data-testid="reminder-add"
                                >
                                    Adicionar lembrete
                                </Button>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={notificationConfigDraft.confirmationsEnabled}
                                    onChange={(e) => handleNotificationChange('confirmationsEnabled', e.target.checked)}
                                />
                                Confirmação de agendamento
                            </CheckboxLabel>
                            {notificationConfigDraft.confirmationsEnabled && (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '12px',
                                        width: '100%',
                                    }}
                                >
                                    <InputGroup>
                                        <LabelRow>Dias antes</LabelRow>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={notificationConfigDraft.confirmationDaysBefore}
                                            onChange={(e) =>
                                                handleNotificationChange(
                                                    'confirmationDaysBefore',
                                                    Number(e.target.value),
                                                )
                                            }
                                        />
                                    </InputGroup>
                                    <InputGroup>
                                        <LabelRow>Horário</LabelRow>
                                        <Input
                                            type="time"
                                            value={notificationConfigDraft.confirmationTime}
                                            onChange={(e) =>
                                                handleNotificationChange('confirmationTime', e.target.value)
                                            }
                                        />
                                    </InputGroup>
                                </div>
                            )}
                        </div>
                    </FormGroup>
                    {business && !hasPaidAccess(business) && (
                        <UpgradeHint>
                            <p>
                                Sem uma assinatura ativa, lembretes e confirmações não são enviados aos seus clientes.
                                {business.subscription?.isTrialEligible && <strong> Teste grátis por 7 dias.</strong>}
                            </p>
                            <Button
                                type="button"
                                onClick={() => {
                                    closeNotificationsModal();
                                    window.history.pushState({}, '', '/configuration/plan?upgrade=solo');
                                    window.dispatchEvent(new PopStateEvent('popstate'));
                                }}
                            >
                                {business.subscription?.isTrialEligible ? 'Testar grátis' : 'Reativar'}
                            </Button>
                        </UpgradeHint>
                    )}
                    {business && hasPaidAccess(business) && !business.whatsapp && (
                        <UpgradeHint>
                            <p>
                                Lembretes e confirmações passam a ser enviados aos seus clientes assim que você conectar
                                o WhatsApp.
                            </p>
                        </UpgradeHint>
                    )}
                </form>
            </Modal>

            {/* Opening Hours Modal */}
            <Modal
                isOpen={isOpeningHoursModalOpen}
                onClose={closeOpeningHoursModal}
                title="Horário de Funcionamento"
                footer={
                    <>
                        <Button
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_opening_hours_cancel_click');
                                closeOpeningHoursModal();
                            }}
                            disabled={openingHoursSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="modal-save"
                            primary
                            type="submit"
                            form="opening-hours-form"
                            disabled={openingHoursSaving}
                        >
                            {openingHoursSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="opening-hours-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveOpeningHours();
                    }}
                >
                    <OpeningHoursEditor
                        value={openingHoursDraft}
                        onChange={setOpeningHoursDraft}
                        disabled={openingHoursSaving}
                        reference={null}
                        closedLabel="Fechado"
                    />
                </form>
            </Modal>

            <Modal
                isOpen={isIntakeModalOpen}
                onClose={closeIntakeModal}
                title="Formulários"
                size="wide"
                onBack={selectedIntakeIndex !== null ? () => setSelectedIntakeIndex(null) : undefined}
                data-testid="intake-modal"
                footer={
                    <>
                        <Button
                            type="button"
                            data-testid="modal-cancel"
                            onClick={() => {
                                gtagEvent('config_intake_cancel_click');
                                closeIntakeModal();
                            }}
                            disabled={intakeSaving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            data-testid="modal-save"
                            primary
                            onClick={handleSaveIntakes}
                            disabled={intakeSaving}
                        >
                            {intakeSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <HelperText>
                        Crie formulários que serão vinculados a serviços. Quando um cliente agenda um serviço com
                        formulário, ele preenche antes da consulta.
                    </HelperText>

                    {intakeDrafts.length === 0 ? (
                        <EmptyState>
                            <HelperText style={{ margin: 0 }}>Nenhum formulário cadastrado.</HelperText>
                            <Button
                                type="button"
                                primary
                                onClick={addNewIntake}
                                disabled={intakeSaving}
                                data-testid="intake-add-new"
                            >
                                Novo formulário
                            </Button>
                        </EmptyState>
                    ) : (
                        <MasterDetail $hasSelection={selectedIntakeIndex !== null}>
                            <MasterList>
                                {intakeDrafts.map((draft, index) => {
                                    const count = draft.questions.length;
                                    return (
                                        <MasterItem
                                            key={`${draft.id ?? 'new'}-${index}`}
                                            $active={selectedIntakeIndex === index}
                                            onClick={() => setSelectedIntakeIndex(index)}
                                            data-testid={`intake-list-item-${index}`}
                                        >
                                            <span>{draft.name || '(sem nome)'}</span>
                                            <MasterItemMeta>
                                                {count} pergunta{count === 1 ? '' : 's'}
                                            </MasterItemMeta>
                                        </MasterItem>
                                    );
                                })}
                                <Button
                                    type="button"
                                    onClick={addNewIntake}
                                    disabled={intakeSaving}
                                    data-testid="intake-add-new"
                                    style={{ marginTop: 6 }}
                                >
                                    Novo formulário
                                </Button>
                            </MasterList>

                            <DetailPanel>
                                {selectedIntakeIndex === null || !intakeDrafts[selectedIntakeIndex] ? (
                                    <DetailPlaceholder>Selecione um formulário ou crie um novo.</DetailPlaceholder>
                                ) : (
                                    <>
                                        <InputGroup>
                                            Nome do formulário
                                            <Input
                                                value={intakeDrafts[selectedIntakeIndex].name}
                                                onChange={(e) =>
                                                    updateIntakeDraft(selectedIntakeIndex, { name: e.target.value })
                                                }
                                                data-testid="intake-name-input"
                                            />
                                        </InputGroup>

                                        {intakeDrafts[selectedIntakeIndex].questions.map((question, qi) => (
                                            <div
                                                key={qi}
                                                style={{
                                                    border: `1px solid ${vars.dividerColor}`,
                                                    borderRadius: 8,
                                                    padding: 12,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10,
                                                }}
                                                data-testid={`intake-question-${qi}`}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                        Pergunta {qi + 1}
                                                    </span>
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        <Button
                                                            type="button"
                                                            iconOnly
                                                            onClick={() =>
                                                                moveIntakeQuestion(selectedIntakeIndex, qi, 'up')
                                                            }
                                                            disabled={qi === 0}
                                                            aria-label="Subir"
                                                            data-testid={`intake-move-up-${qi}`}
                                                        >
                                                            ▲
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            iconOnly
                                                            onClick={() =>
                                                                moveIntakeQuestion(selectedIntakeIndex, qi, 'down')
                                                            }
                                                            disabled={
                                                                qi ===
                                                                intakeDrafts[selectedIntakeIndex].questions.length - 1
                                                            }
                                                            aria-label="Descer"
                                                            data-testid={`intake-move-down-${qi}`}
                                                        >
                                                            ▼
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            iconOnly
                                                            danger
                                                            onClick={() =>
                                                                removeIntakeQuestion(selectedIntakeIndex, qi)
                                                            }
                                                            aria-label="Remover"
                                                            data-testid={`intake-delete-${qi}`}
                                                        >
                                                            －
                                                        </Button>
                                                    </div>
                                                </div>

                                                <InputGroup>
                                                    Texto da pergunta
                                                    <Input
                                                        value={question.questionText}
                                                        onChange={(e) =>
                                                            updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                questionText: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Ex: Como nos conheceu?"
                                                        data-testid={`intake-question-text-${qi}`}
                                                    />
                                                </InputGroup>

                                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                                    <InputGroup style={{ flex: 1, minWidth: 140 }}>
                                                        Tipo
                                                        <Select
                                                            value={question.type}
                                                            onChange={(e) =>
                                                                updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                    type: e.target.value as IntakeQuestionDraft['type'],
                                                                    options:
                                                                        e.target.value === 'enum'
                                                                            ? question.options.length > 0
                                                                                ? question.options
                                                                                : ['']
                                                                            : [],
                                                                })
                                                            }
                                                            data-testid={`intake-question-type-${qi}`}
                                                        >
                                                            <option value="text">Texto</option>
                                                            <option value="textarea">Texto longo</option>
                                                            <option value="boolean">Sim ou Não</option>
                                                            <option value="enum">Escolha</option>
                                                        </Select>
                                                    </InputGroup>

                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-end',
                                                            paddingBottom: 4,
                                                        }}
                                                    >
                                                        <CheckboxLabel>
                                                            <Checkbox
                                                                checked={question.required}
                                                                onChange={(e) =>
                                                                    updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                        required: e.target.checked,
                                                                    })
                                                                }
                                                                data-testid={`intake-question-required-${qi}`}
                                                            />
                                                            Obrigatória
                                                        </CheckboxLabel>
                                                    </div>
                                                </div>

                                                {question.type === 'enum' && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                                            Opções
                                                        </span>
                                                        {question.options.map((option, optIndex) => (
                                                            <div
                                                                key={optIndex}
                                                                style={{
                                                                    display: 'flex',
                                                                    gap: 6,
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <Input
                                                                    value={option}
                                                                    onChange={(e) => {
                                                                        const newOptions = [...question.options];
                                                                        newOptions[optIndex] = e.target.value;
                                                                        updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                            options: newOptions,
                                                                        });
                                                                    }}
                                                                    placeholder={`Opção ${optIndex + 1}`}
                                                                    style={{ flex: 1 }}
                                                                    data-testid={`intake-option-${qi}-${optIndex}`}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    iconOnly
                                                                    danger
                                                                    onClick={() => {
                                                                        const newOptions = question.options.filter(
                                                                            (_, i) => i !== optIndex,
                                                                        );
                                                                        updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                            options: newOptions,
                                                                        });
                                                                    }}
                                                                    aria-label="Remover opção"
                                                                >
                                                                    －
                                                                </Button>
                                                            </div>
                                                        ))}
                                                        <Button
                                                            type="button"
                                                            onClick={() =>
                                                                updateIntakeQuestion(selectedIntakeIndex, qi, {
                                                                    options: [...question.options, ''],
                                                                })
                                                            }
                                                            style={{ alignSelf: 'flex-start' }}
                                                        >
                                                            Adicionar opção
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        <Button
                                            type="button"
                                            onClick={() => addIntakeQuestion(selectedIntakeIndex)}
                                            style={{ alignSelf: 'flex-start' }}
                                            data-testid="intake-add-question"
                                        >
                                            Adicionar pergunta
                                        </Button>

                                        <Divider />

                                        <Button
                                            danger
                                            type="button"
                                            onClick={() => handleRequestRemoveIntake(intakeDrafts[selectedIntakeIndex])}
                                            style={{ alignSelf: 'flex-start' }}
                                            data-testid="intake-remove"
                                        >
                                            Remover formulário
                                        </Button>
                                    </>
                                )}
                            </DetailPanel>
                        </MasterDetail>
                    )}
                </div>
            </Modal>

            <OwnerPhoneWarningDialog
                isOpen={phoneEditWarningOpen}
                onCancel={() => setPhoneEditWarningOpen(false)}
                onContinue={() => {
                    setPhoneEditWarningOpen(false);
                    setPhoneEditModalOpen(true);
                }}
            />

            {business && service && (
                <OwnerPhoneChangeModal
                    isOpen={phoneEditModalOpen}
                    onClose={() => setPhoneEditModalOpen(false)}
                    onSuccess={handleOwnerPhoneChanged}
                    service={service}
                    currentOwnerPhoneNumber={business.owner.phoneNumber}
                />
            )}

            {intakeRemoveTarget &&
                (() => {
                    const bound = getServicesBoundToIntake(intakeRemoveTarget.id);
                    const impactLine =
                        bound.length === 0 ? null : bound.length === 1 ? (
                            <>
                                O serviço vinculado ficará sem formulário: <strong>{bound[0]}</strong>.
                            </>
                        ) : (
                            <>
                                Os {bound.length} serviços vinculados ficarão sem formulário:{' '}
                                <strong>{bound.join(', ')}</strong>.
                            </>
                        );
                    return (
                        <ConfirmDialog
                            isOpen={true}
                            title="Remover formulário"
                            confirmText="Remover"
                            message={
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <p style={{ margin: 0 }}>
                                        Tem certeza que deseja remover o formulário{' '}
                                        <strong>{intakeRemoveTarget.name}</strong>?
                                    </p>
                                    {impactLine && <p style={{ margin: 0 }}>{impactLine}</p>}
                                </div>
                            }
                            onCancel={() => setIntakeRemoveTarget(null)}
                            onConfirm={handleConfirmRemoveIntake}
                            isLoading={intakeSaving}
                        />
                    );
                })()}
        </Container>
    );
}

const EditIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

const EditPhoneButton = styled(Button)`
    border-color: ${vars.dividerColor};

    &:not(:disabled):hover {
        border-color: ${vars.foregroundColor};
    }
`;

function OwnerPhoneWarningDialog({
    isOpen,
    onCancel,
    onContinue,
}: {
    isOpen: boolean;
    onCancel: () => void;
    onContinue: () => void;
}) {
    return (
        <Dialog isOpen={isOpen} onClose={onCancel} data-testid="owner-phone-warning">
            <DialogHeader>
                <DialogTitle>Alterar número de WhatsApp</DialogTitle>
            </DialogHeader>
            <DialogContent>
                Este número também é usado para entrar no painel. Ao alterá-lo, o login passará a ser feito com o novo
                número.
            </DialogContent>
            <DialogFooter>
                <Button type="button" onClick={onCancel} data-testid="owner-phone-warning-cancel">
                    Cancelar
                </Button>
                <Button type="button" warning primary onClick={onContinue} data-testid="owner-phone-warning-continue">
                    Continuar
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
